import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'next/navigation';
import { CurrencyHelpers } from 'helpers/currencyHelpers';
import useI18n from 'helpers/hooks/useI18n';
import { FacetProps } from './types';
import { useProductList } from '../../context';
import { refinementRemovedEventName, refinementsClearedEventName } from '../../context/constants';
import { RefinementRemovedEvent } from '../../context/types';
import { RangeFacet as RangeFacetType } from '../../types';

const RangeFacet: React.FC<FacetProps> = ({ attribute }) => {
  const { facetsConfiguration, refineRange } = useProductList();
  const { currency } = useI18n();
  const { locale } = useParams();
  const refine = useCallback((value: [number, number]) => refineRange(attribute, value), [attribute, refineRange]);
  const facet = useMemo(() => facetsConfiguration[attribute] as RangeFacetType, [facetsConfiguration, attribute]);

  const [priceRange, setPriceRange] = useState({
    min: (facet?.minSelected ?? facet.min) / 100,
    max: (facet?.maxSelected ?? facet.max) / 100,
  });

  const applyRefinement = useCallback(
    (appliedRange: typeof priceRange) => {
      const min = Math.max(0, appliedRange.min);
      const max = Math.min(appliedRange.max, facet.max);

      refine([min, max]);
    },
    [refine, facet],
  );

  const reset = useCallback(() => {
    setPriceRange({ min: facet.min / 100, max: facet.max / 100 });
  }, [facet]);

  const handleRefinementRemoved = useCallback(
    (e: CustomEvent<RefinementRemovedEvent>) => {
      if (e.detail.attribute === attribute) reset();
    },
    [reset, attribute],
  );

  useEffect(() => {
    setPriceRange({
      min: facet?.minSelected ? facet.minSelected / 100 : facet.min / 100,
      max: facet?.maxSelected ? facet.maxSelected / 100 : facet.max / 100,
    });
  }, [facet?.minSelected, facet?.maxSelected, facet?.min, facet?.max]);

  useEffect(() => {
    window.addEventListener(refinementRemovedEventName, handleRefinementRemoved as EventListener);
    window.addEventListener(refinementsClearedEventName, reset);

    return () => {
      window.removeEventListener(refinementRemovedEventName, handleRefinementRemoved as EventListener);
      window.removeEventListener(refinementsClearedEventName, reset);
    };
  }, [reset, handleRefinementRemoved]);

  const handleRangeSubmit = useCallback(() => {
    applyRefinement({ min: priceRange.min * 100, max: priceRange.max * 100 });
  }, [priceRange, applyRefinement]);

  const min = facet.min / 100;
  const max = facet.max / 100;
  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = Number(e.target.value);
    setPriceRange({ min: min, max: newValue });
  };

  const percentage = Math.min(Math.max(((priceRange.max - min) / (max - min)) * 100, 0), 100);

  return (
    <div className="mt-10 flex w-full flex-col items-center">
      <div className="relative w-full">
        <div className="relative my-16 flex h-8">
          <div className="absolute top-1/2 h-8 w-full -translate-y-1/2 rounded-full bg-gray-300" />
          <div
            className="absolute top-1/2 h-8 rounded-full bg-orange-500"
            style={{ width: `${percentage}%`, transform: 'translateY(-50%)' }}
          />
          <input
            type="range"
            aria-label="Price range filter"
            min={min}
            max={max}
            value={priceRange.max}
            onChange={handleChange}
            onMouseUp={handleRangeSubmit}
            className="relative h-8 w-full cursor-pointer appearance-none bg-transparent"
            style={{
              WebkitAppearance: 'none',
              appearance: 'none',
            }}
          />
        </div>
        <div className="relative mt-2 flex w-full justify-between text-sm font-medium text-gray-900">
          <span>{CurrencyHelpers.formatForCurrency(min, locale, currency)}</span>
          <span className="font-bold text-orange-500">
            {CurrencyHelpers.formatForCurrency(priceRange.max, locale, currency)}
          </span>
          <span>{CurrencyHelpers.formatForCurrency(max, locale, currency)}</span>
        </div>
      </div>
    </div>
  );
};

export default RangeFacet;
